import { ROTULO_DE_SITUACAO, type SituacaoDeRelease } from '@/features/releases/tipos';

/**
 * O ciclo de vida da versão de entrega, do lado da tela.
 *
 * ## Por que a tabela está duplicada aqui
 *
 * Ela é **espelho** de `apps/api/src/itsm/releases/release-lifecycle.ts`, e a
 * duplicação é deliberada porque não há alternativa: o servidor não expõe o
 * grafo — só recusa a transição inválida com 409 e uma frase que nomeia as
 * saídas. Sem a cópia, a tela teria de oferecer os oito botões e deixar sete
 * falharem, o que é pior do que uma tabela de dez linhas mantida em dois
 * lugares.
 *
 * A cópia é segura porque o servidor continua sendo a autoridade: a tela erra
 * para menos (deixa de oferecer algo que passaria), nunca para mais (o que ela
 * oferecer a mais é recusado com a mensagem do servidor, que a tela mostra).
 *
 * ★ Quando a API ganhar uma rota que devolva as transições possíveis — como
 * `GET /tickets/:id/transitions` já faz para o chamado —, este arquivo vira
 * duas linhas de leitura e a tabela some.
 */

/**
 * Voltar é permitido até `READY`: teste que reprova devolve a versão para
 * construção, e proibir isso só ensinaria a equipe a criar release nova a cada
 * correção — o que destruiria o histórico que a release existe para dar.
 */
const TRANSICOES: Readonly<Record<SituacaoDeRelease, readonly SituacaoDeRelease[]>> = {
  PLANNING: ['BUILD', 'CANCELLED'],
  BUILD: ['TESTING', 'PLANNING', 'CANCELLED'],
  TESTING: ['READY', 'BUILD', 'CANCELLED'],
  READY: ['DEPLOYING', 'TESTING', 'CANCELLED'],
  DEPLOYING: ['DEPLOYED', 'READY', 'ROLLED_BACK'],
  DEPLOYED: ['ROLLED_BACK'],
  ROLLED_BACK: [],
  CANCELLED: [],
};

export const TERMINAIS: ReadonlySet<SituacaoDeRelease> = new Set<SituacaoDeRelease>([
  'ROLLED_BACK',
  'CANCELLED',
]);

export function transicoesDe(atual: SituacaoDeRelease): readonly SituacaoDeRelease[] {
  return TRANSICOES[atual];
}

/**
 * As transições que a tela oferece como **botão**.
 *
 * `CANCELLED` e `ROLLED_BACK` saem daqui de propósito: as duas têm rota própria
 * e pedem confirmação — reverter exige motivo com no mínimo quinze caracteres,
 * e um botão numa fileira de cinco não é lugar de pedir isso.
 */
export function avancosDe(atual: SituacaoDeRelease): SituacaoDeRelease[] {
  return TRANSICOES[atual].filter(
    (destino) => destino !== 'CANCELLED' && destino !== 'ROLLED_BACK',
  );
}

/** Pode reverter? Só de dentro da implantação ou depois dela. */
export const podeReverter = (atual: SituacaoDeRelease): boolean =>
  TRANSICOES[atual].includes('ROLLED_BACK');

export const podeCancelar = (atual: SituacaoDeRelease): boolean =>
  TRANSICOES[atual].includes('CANCELLED');

/**
 * Aceita mudança nova?
 *
 * Espelha `assertComponivel` do serviço: o conteúdo de uma versão que já subiu
 * é histórico, e acrescentar item depois faria "a 2026.09 continha estas sete
 * mudanças" ser uma frase sem valor.
 */
export const ehComponivel = (atual: SituacaoDeRelease): boolean =>
  !TERMINAIS.has(atual) && atual !== 'DEPLOYING' && atual !== 'DEPLOYED';

/** Por que não dá para mexer no conteúdo — a frase que a tela mostra. */
export function motivoDeNaoCompor(atual: SituacaoDeRelease): string | null {
  if (ehComponivel(atual)) return null;
  if (TERMINAIS.has(atual)) {
    return `A versão está em "${ROTULO_DE_SITUACAO[atual].toLowerCase()}", que é situação final: o conteúdo dela é histórico.`;
  }
  return `A implantação já começou (${ROTULO_DE_SITUACAO[atual].toLowerCase()}). Mudança nova entra numa versão nova — do contrário, "esta entrega continha estas mudanças" deixa de significar alguma coisa.`;
}

/** A ordem em que as situações se leem numa linha do tempo. */
export const ORDEM_DO_CICLO: readonly SituacaoDeRelease[] = [
  'PLANNING',
  'BUILD',
  'TESTING',
  'READY',
  'DEPLOYING',
  'DEPLOYED',
];

/**
 * Onde a versão está na linha do tempo, de 0 a `ORDEM_DO_CICLO.length - 1`.
 *
 * Terminal fora do caminho feliz devolve `-1`: revertida e cancelada não são
 * "um passo adiante de implantada", e desenhá-las na mesma régua daria a
 * impressão de progresso onde houve o contrário.
 */
export const posicaoNoCiclo = (atual: SituacaoDeRelease): number =>
  ORDEM_DO_CICLO.indexOf(atual);
